import { useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Loader, Center } from "@mantine/core";
import { AuthContext } from "../services/context";
import { getCurrentWorkspace } from "../services/userConfig";
import BadgeBuilder from "./BadgeBuilder";
import { useGetBadgeTemplate, useUpdateBadgeTemplate } from "../api/badges";
import { useFileUpload, queryClient } from "../services/react-query";

const round2 = (n) => parseFloat(n.toFixed(2));

function EditBadge() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const currentWorkspace = getCurrentWorkspace(user);
  const eventId = currentWorkspace.eventId;

  const { data: badge, isPending } = useGetBadgeTemplate(id);
  const { mutate: updateBadge, isPending: isSaving } = useUpdateBadgeTemplate({
    id,
  });
  const { mutateAsync: uploadFile } = useFileUpload();

  const handleCancel = () => navigate("/app/badges");

  const handleSubmit = async (builderData) => {
    let backgroundKey;
    let backgroundChanged = false;

    if (builderData.bgFile) {
      const uploadResult = await uploadFile(builderData.bgFile);
      backgroundKey = uploadResult.fileKey;
      backgroundChanged = true;
    } else if (!builderData.bgImage) {
      backgroundKey = null;
      backgroundChanged = true;
    }

    const templateData = {
      size: {
        width: builderData.size.width,
        height: builderData.size.height,
        unit: builderData.size.unit,
      },
      hasBackground: !!builderData.bgImage,
      backgroundTransform: builderData.backgroundTransform,
      elements: builderData.elements.map((el) => {
        const base = {
          id: el.id,
          type: el.type,
          layer: el.layer,
          xPct: round2(el.xPct),
          yPct: round2(el.yPct),
          widthPct: round2(el.widthPct),
          heightPct: round2(el.heightPct),
        };
        if (el.type === "field") base.fieldKey = el.fieldKey;
        if (el.type === "text") base.text = el.text;
        if (el.type === "text" || el.type === "field") {
          base.fontSize = el.fontSize;
          base.color = el.color;
          base.align = el.align;
        }
        if (el.type === "rectangle") base.fill = el.fill;
        return base;
      }),
    };

    const payload = {
      eventId,
      name: badge.name,
      templateData,
    };
    if (backgroundChanged) payload.backgroundKey = backgroundKey;

    updateBadge(payload, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["badgeTemplates"] });
        navigate("/app/badges");
      },
    });
  };

  if (isPending) {
    return (
      <Center style={{ height: "calc(100vh - 100px)" }}>
        <Loader size="xl" />
      </Center>
    );
  }

  const templateData = badge?.templateData || {};

  const initialValue = {
    size: templateData.size || { width: 4, height: 3, unit: "in" },
    bgImage: badge?.backgroundKey || null,
    backgroundTransform: templateData.backgroundTransform || { fit: "fit" },
    elements: (templateData.elements || []).map((el, index) => ({
      ...el,
      id: el.id || `el-${index}`,
    })),
  };

  return (
    <Container fluid p={0}>
      <BadgeBuilder
        initialValue={initialValue}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        saving={isSaving}
      />
    </Container>
  );
}

export default EditBadge;
